// Title derivation for occurrence rows in the grouped deliverables
// table. Child rows surface the occurrence period (e.g. "Apr 2026" for
// monthly, "Q2 2026" for quarterly) rather than repeating the parent's
// name, so the grouped view scans as a timeline rather than duplicated
// text. Standalone (non-parent-linked) rows keep their own name.
//
// When the caller knows the parent, it passes it through so the format
// can follow the parent's recurrence frequency. Without a parent, the
// helper falls back to the monthly format ("Apr 2026") — this
// keeps legacy callers (e.g. the summary strip's next-earliest lookup)
// working without modification.

import type { Deliverable } from "../../../../types";

function parseIsoUtc(iso: string): Date | null {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatMonthYear(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  return d.toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatFullDate(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatWeekOf(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  // Move to the Monday of the week in which `d` falls (ISO week start).
  // getUTCDay: Sun=0, Mon=1, ... — offset to Monday.
  const day = d.getUTCDay();
  const offset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + offset);
  const datePart = monday.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `Wk of ${datePart}`;
}

function formatQuarter(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  const month = d.getUTCMonth(); // 0-based
  const quarter = Math.floor(month / 3) + 1;
  return `Q${quarter} ${d.getUTCFullYear()}`;
}

function formatYear(iso: string): string | null {
  const d = parseIsoUtc(iso);
  if (!d) return null;
  return String(d.getUTCFullYear());
}

export function deriveOccurrenceTitle(
  row: Deliverable,
  parent?: Deliverable | null,
): string {
  if (!row.parentId) return row.name;
  const periodDate = row.occurrenceDate ?? row.dueDate;
  if (!periodDate) return row.name;

  const frequency = parent?.recurrenceRule?.frequency;
  switch (frequency) {
    case "daily":
      return formatFullDate(periodDate) ?? row.name;
    case "weekly":
      return formatWeekOf(periodDate) ?? row.name;
    case "quarterly":
      return formatQuarter(periodDate) ?? row.name;
    case "annually":
      return formatYear(periodDate) ?? row.name;
    case "monthly":
    default:
      return formatMonthYear(periodDate) ?? row.name;
  }
}
